import React from 'react'
import type { ICellRendererParams } from 'ag-grid-community'
import { Horizontal, Vertical, Texto, MaterialIcon, GraviButton } from '@gravitate-js/excalibrr'
import { Tooltip } from 'antd'
import { createSearchParams, Link, useNavigate } from 'react-router-dom'
import type { Match, Warning } from '../../../../../../api/useSupplierReconciliation/types/match'
import dayjs from 'dayjs'

const BolNumberWithLink = ({
  bolNumber,
  bolId,
  data,
  hasMovementValidationError,
}: { bolNumber: string; bolId: string | null; data: Match; hasMovementValidationError?: boolean }) => {
  if (!bolId) {
    return (
      <Tooltip title='Expected BOL is missing' placement='top'>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'default' }}>
          <MaterialIcon icon='warning' style={{ color: 'var(--theme-error)', fontSize: 14 }} />
          <Texto category='p2' appearance='hint'>
            {bolNumber}
          </Texto>
        </div>
      </Tooltip>
    )
  }

  const orderNumber = data.order_numbers?.[0]?.number

  if (!orderNumber) {
    return (
      <Texto category='p2' appearance='hint'>
        {bolNumber}
      </Texto>
    )
  }

  return (
    <span
      style={{
        color: hasMovementValidationError ? 'white' : 'var(--theme-color-2)',
        cursor: 'pointer',
        textDecoration: 'underline',
      }}
      onClick={() => {
        window.open(`/order_movements_page/report?${new URLSearchParams({ order_number: orderNumber })}`, '_blank')
      }}
    >
      {bolNumber}
    </span>
  )
}

export function BolNumbersCellRenderer({ data, context }: ICellRendererParams<Match>) {
  const bolNumbers = data?.bol_numbers || []
  const hasMovementValidationError = data?.warnings?.some((w: Warning) => w.type === 'movement_validation')
  const hasDeliveryReconciliationError = data?.warnings?.some((w: Warning) => w.type === 'delivery_reconciliation')

  // Always show error styling for delivery reconciliation errors (regardless of blocking mode)
  // Only the approval functionality should be affected by blocking mode, not the visual warnings
  const hasErrorStyling = hasMovementValidationError || hasDeliveryReconciliationError

  if (!bolNumbers.length)
    return (
      <Tooltip title='Find BOL Match'>
        <GraviButton appearance='link' style={{ height: 'auto', padding: 0 }}>
          <Horizontal verticalCenter style={{ overflow: 'visible' }} gap={4}>
            <MaterialIcon style={{ color: 'var(--theme-error)' }} icon='warning' />
            <Texto appearance='hint'>N/A</Texto>
          </Horizontal>
        </GraviButton>
      </Tooltip>
    )

  const cellContent = (
    <Horizontal verticalCenter gap={8}>
      {hasErrorStyling && <MaterialIcon icon='error' style={{ color: 'white', fontSize: 16 }} />}
      <Vertical justifyContent='center' style={{ gap: '4px', overflow: 'visible' }}>
        {bolNumbers.map((bol, index) => (
          <BolNumberWithLink
            key={bol.id || `${bol.number}-${index}`}
            bolNumber={bol.number}
            bolId={bol.id}
            data={data}
            hasMovementValidationError={hasErrorStyling}
          />
        ))}
      </Vertical>
    </Horizontal>
  )

  const getTooltipMessage = () => {
    const messages = []
    if (hasMovementValidationError) {
      messages.push('This BOL has order movement validation issues')
    }
    if (hasDeliveryReconciliationError) {
      messages.push(<DeliveryReconciliationErrorTooltip data={data} />)
    }
    if (messages.length === 0) return null
    if (messages.length === 1) return messages[0]
    return (
      <>
        This BOL has multiple issues:
        <ul className='pl-4' style={{ margin: '2px 0' }}>
          {messages.map((msg, idx) => (
            <li key={idx}>{msg}</li>
          ))}
        </ul>
      </>
    )
  }

  const tooltipMessage = getTooltipMessage()

  if (tooltipMessage) {
    return (
      <Tooltip title={tooltipMessage} placement='top' styles={{ root: { width: 'auto', maxWidth: 'none' } }}>
        <div style={{ width: '100%', height: '100%' }}>{cellContent}</div>
      </Tooltip>
    )
  }

  return cellContent
}

const toSearch = (data?: Match) => {
  if (!data) return
  return `?${createSearchParams({
    site: data.destinations?.length ? data.destinations.map((d) => d.site_number) : [],
    date: data.bol_date
      ? [dayjs(data.bol_date).subtract(1, 'day').toISOString(), dayjs(data.bol_date).add(1, 'day').toISOString()]
      : [],
  })}`
}

function DeliveryReconciliationErrorTooltip({ data }: { data: Match | undefined }) {
  return (
    <Horizontal justifyContent='space-between' gap={12}>
      <Texto appearance='white'>This BOL has delivery reconciliation errors</Texto>
      <Link
        to={{
          pathname: '/valuation/delivery_report',
          search: toSearch(data),
        }}
        target='_blank'
        rel='noopener noreferrer'
        style={{ color: 'inherit', textDecoration: 'underline' }}
      >
        View
      </Link>
    </Horizontal>
  )
}
